import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SchoolIcon from '@mui/icons-material/School';
import ArticleIcon from '@mui/icons-material/Article';
import { useAuth } from '../../context/AuthContext';

const Navbar = () => {
    const navigate = useNavigate();
    const { isAuthenticated, logout } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileAnchorEl, setMobileAnchorEl] = useState(null);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMobileMenu = (event) => {
        setMobileAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setMobileAnchorEl(null);
    };

    const handleLogout = () => {
        logout();
        handleClose();
        navigate('/login');
    };

    const menuItems = [
        { text: 'Dashboard', path: '/dashboard', auth: true },
        { text: 'Browse Exams', path: '/exams', auth: true },
        { text: 'Articles', path: '/articles', auth: false },
        { text: 'About', path: '/about', auth: false },
    ];

    const renderMobileMenuItems = () => {
        const items = [];
        
        // Add main menu items
        menuItems.forEach((item) => {
            if (!item.auth || isAuthenticated) {
                items.push(
                    <MenuItem
                        key={item.text}
                        component={RouterLink}
                        to={item.path}
                        onClick={handleClose}
                    >
                        {item.text}
                    </MenuItem>
                );
            }
        });

        // Add auth menu items if not authenticated
        if (!isAuthenticated) {
            items.push(
                <Divider key="auth-divider" />,
                <MenuItem
                    key="login"
                    component={RouterLink}
                    to="/login"
                    onClick={handleClose}
                >
                    Login
                </MenuItem>,
                <MenuItem
                    key="register"
                    component={RouterLink}
                    to="/register"
                    onClick={handleClose}
                >
                    Register
                </MenuItem>
            );
        } else {
            // Add Settings and Logout options for authenticated users
            items.push(
                <Divider key="user-divider" />,
                <MenuItem
                    key="settings"
                    component={RouterLink}
                    to="/settings"
                    onClick={handleClose}
                >
                    Settings
                </MenuItem>,
                <MenuItem
                    key="logout"
                    onClick={handleLogout}
                >
                    Logout
                </MenuItem>
            );
        }

        return items;
    };

    return (
        <AppBar position="sticky">
            <Toolbar>
                <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={{ mr: 2, display: { sm: 'none' } }}
                    onClick={handleMobileMenu}
                >
                    <MenuIcon />
                </IconButton>

                <Typography
                    variant="h6"
                    component={RouterLink}
                    to="/"
                    sx={{
                        flexGrow: 1,
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <SchoolIcon />
                    Medical Learn
                </Typography>

                {/* Desktop Menu */}
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2 }}>
                    {menuItems.map((item) => (
                        (!item.auth || isAuthenticated) && (
                            <Button
                                key={item.text}
                                color="inherit"
                                component={RouterLink}
                                to={item.path}
                                startIcon={item.text === 'Articles' ? <ArticleIcon /> : null}
                            >
                                {item.text}
                            </Button>
                        )
                    ))}
                </Box>

                {/* Auth Buttons */}
                <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
                    {isAuthenticated ? (
                        <>
                            <IconButton
                                onClick={handleMenu}
                                color="inherit"
                            >
                                <Avatar sx={{ width: 32, height: 32 }} />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                            >
                                <MenuItem component={RouterLink} to="/settings" onClick={handleClose}>
                                    Settings
                                </MenuItem>
                                <MenuItem onClick={handleLogout}>Logout</MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <>
                            <Button color="inherit" component={RouterLink} to="/login">
                                Login
                            </Button>
                            <Button color="inherit" component={RouterLink} to="/register">
                                Register
                            </Button>
                        </>
                    )}
                </Box>

                {/* Mobile Menu */}
                <Menu
                    anchorEl={mobileAnchorEl}
                    open={Boolean(mobileAnchorEl)}
                    onClose={handleClose}
                >
                    {renderMobileMenuItems()}
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

export default Navbar; 